import { Box, Typography, AccordionSummary, styled } from '@mui/material';
import { capitalizedLowercase } from 'src/utils/string';
import { ChipWrapper } from 'src/style/common';
import Chip from 'src/frontend-common/components/Chip';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta, MutationDefinition } from '@reduxjs/toolkit/dist/query';
import { MutationTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { ResolutionIdRequest, ResolutionIdResponse, ScoreRequest } from 'src/redux/services/score';
import { IGenericFormValues } from '../utils';

// Helper function to determine the field label
const getFieldLabel = (fieldName: string): string => {
  const field = capitalizedLowercase(fieldName);
  switch (field) {
    case 'User_agent':
      return 'Device';
    case 'Ip':
      return 'IP Address';
    case 'Email':
      return 'Email Address';
    case 'Phone':
      return 'Phone Number';
    default:
      return field;
  }
};

export const renderPreset = (fieldName: string, setResolutionId: React.Dispatch<React.SetStateAction<string | undefined>>) => (
  <ChipWrapper item xs={12} md={6}>
    <Typography variant="h5" color="text.secondary">
      Try an example:
    </Typography>

    {/* Bad Example */}
    <Box data-testid="preset-bad-button" style={{ display: 'inline-block', cursor: 'pointer' }} onClick={() => setResolutionId('SCORE-SIMULATOR-MOCK-BAD')}>
      <Chip variant="status" size="small" status="error">
        {`Bad ${getFieldLabel(fieldName)}`}
      </Chip>
    </Box>

    {/* Good Example */}
    <Box style={{ display: 'inline-block', cursor: 'pointer' }} data-testid="preset-good-button" onClick={() => setResolutionId('SCORE-SIMULATOR-MOCK-GOOD')}>
      <Chip variant="status" size="small" status="success">
        {`Good ${getFieldLabel(fieldName)}`}
      </Chip>
    </Box>
  </ChipWrapper>
);

export const AccordionSummaryWrapper = styled(AccordionSummary)(() => ({
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: '10px',
  },
}));

interface ScoreFormProps {
  apiKey: string;
  jsKey: string;
  ip: string;
  fieldName: string;
  isLoading: boolean;
  setResolutionId: React.Dispatch<React.SetStateAction<string | undefined>>;
  updateScore: MutationTrigger<
    MutationDefinition<
      ScoreRequest,
      BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta>,
      never,
      ResolutionIdResponse,
      'scoreApi'
    >
  >;
  updateResolutionId: MutationTrigger<
    MutationDefinition<
      ResolutionIdRequest,
      BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta>,
      never,
      ResolutionIdResponse,
      'scoreApi'
    >
  >;
  changeTab: (_event: unknown, newValue: number, dontChangeDefault?: boolean) => void;
  defaultValue: string | null;
  setDefaultValue: React.Dispatch<React.SetStateAction<string | null>>;
}

export type GenericFormProps = ScoreFormProps & {
  generalFormInitialValues: IGenericFormValues;
  setGeneralFormInitialValues: React.Dispatch<React.SetStateAction<IGenericFormValues>>;
};

export type EnrichmentTime = 'minimal' | 'normal' | 'extensive';

export const getEnrichmentTime = (enrichmentTime: EnrichmentTime) => {
  switch (enrichmentTime) {
    case 'minimal':
      return 1.7;
    case 'normal':
      return 3;
    case 'extensive':
      return undefined;
    default:
      return undefined;
  }
};

export const removeFidoObject = () => {
  window.fido = undefined;
  window.fidoObject = undefined;
  const nodes: any = document.querySelectorAll(`[src='${process.env.REACT_APP_FIDO_JS_SIGNUP_URL}']`);

  if (nodes[0]) document.head.removeChild(nodes[0]);
};
